import React from "react";
import { Plus, X } from "lucide-react";
import { Waveform } from "./Waveform";

export type ClipVariant = "Story" | "Ambient" | "Effet sonore" | "Ajouter" | "AjouterAmbient";

export const CLIP_BASE_H = 58;
export const CLIP_BOTTOM_Y = 83; // track height (88) - bottom padding (5)

export const CLIP_STYLES: Record<
  ClipVariant,
  { bg: string; border: string; text: string; dashed?: boolean }
> = {
  Story: { bg: "#eff6ff", border: "#007aff", text: "#007aff" },
  Ambient: { bg: "#cdffd1", border: "#04a404", text: "#04a404" },
  "Effet sonore": { bg: "#fdebf9", border: "#c8009c", text: "#c8009c" },
  Ajouter: { bg: "transparent", border: "#c8009c", text: "#c8009c", dashed: true },
  AjouterAmbient: { bg: "transparent", border: "#04a404", text: "#04a404", dashed: true },
};

type AudioClipProps = {
  label: string;
  variant: ClipVariant;
  startPx: number;
  widthPx: number;
  heightPx?: number;
  selected?: boolean;
  onClick?: () => void;
  onDragStart?: (e: React.MouseEvent) => void;
  onDurationResizeStart?: (e: React.MouseEvent) => void;
  onStartResizeStart?: (e: React.MouseEvent) => void;
  onDelete?: () => void;
  waveformUrl?: string;
  waveformStartFrac?: number;
  waveformEndFrac?: number;
  cutMode?: boolean;
  onCut?: (clickXInClip: number) => void;
};

// Inline SVG cursor showing scissors, used when the cut tool is active over a cuttable clip.
const SCISSORS_CURSOR =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23c8009c' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><circle cx='6' cy='6' r='3'/><path d='M8.12 8.12 12 12'/><path d='M20 4 8.12 15.88'/><circle cx='6' cy='18' r='3'/><path d='M14.8 14.8 20 20'/></svg>\") 10 10, crosshair";

export function AudioClip({
  label,
  variant,
  startPx,
  widthPx,
  heightPx = CLIP_BASE_H,
  selected,
  onClick,
  onDragStart,
  onDurationResizeStart,
  onStartResizeStart,
  onDelete,
  waveformUrl,
  waveformStartFrac,
  waveformEndFrac,
  cutMode,
  onCut,
}: AudioClipProps) {
  const { bg, border, text, dashed } = CLIP_STYLES[variant];
  const isCuttable = Boolean(cutMode && onCut);
  const isDraggable = Boolean(onDragStart) && !isCuttable;
  const isLengthChangeable = Boolean(onDurationResizeStart) && !isCuttable;
  const isStartChangeable = Boolean(onStartResizeStart) && !isCuttable;
  const topPx = CLIP_BOTTOM_Y - heightPx;
  const showWaveform = !dashed && heightPx >= 24;

  return (
    <div
      className="absolute rounded-[6px] overflow-hidden select-none"
      style={{
        left: startPx,
        top: topPx,
        width: Math.max(widthPx, 36),
        height: heightPx,
        backgroundColor: bg,
        border: `1px ${dashed ? "dashed" : "solid"} ${border}`,
        color: text,
        cursor: isCuttable
          ? SCISSORS_CURSOR
          : isDraggable
          ? "grab"
          : onClick
          ? "pointer"
          : "default",
        boxShadow: selected ? `0 0 0 2px ${border}, 0 0 0 4px #ffffff` : undefined,
      }}
      onClick={!isDraggable && !isLengthChangeable && !isCuttable ? onClick : undefined}
      onMouseDown={(e) => {
        if (isCuttable) {
          // In cut mode, a mousedown anywhere on the clip body splits it.
          e.stopPropagation();
          e.preventDefault();
          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
          onCut!(e.clientX - rect.left);
          return;
        }
        if (onDragStart || onClick || onDurationResizeStart) e.stopPropagation();
        if (onDragStart) onDragStart(e);
      }}
    >
      {/* Start resize handle on the left edge — disabled in cut mode */}
      {isStartChangeable && (
        <div
          className="absolute left-0 top-0 bottom-0 w-2.5 hover:bg-black/10 cursor-ew-resize z-10 flex items-center justify-center"
          onMouseDown={(e) => {
            e.stopPropagation();
            onStartResizeStart!(e);
          }}
        >
          <div
            className="w-[1.5px] h-4 rounded-full"
            style={{ backgroundColor: border, opacity: 0.55 }}
          />
        </div>
      )}

      {/* Duration resize handle on the right edge — disabled in cut mode */}
      {isLengthChangeable && (
        <div
          className="absolute right-0 top-0 bottom-0 w-2.5 hover:bg-black/10 cursor-ew-resize z-10 flex items-center justify-center"
          onMouseDown={(e) => {
            e.stopPropagation();
            onDurationResizeStart!(e);
          }}
        >
          <div
            className="w-[1.5px] h-4 rounded-full"
            style={{ backgroundColor: border, opacity: 0.55 }}
          />
        </div>
      )}

      {/* Delete (cross) — top-right pastille — hidden in cut mode */}
      {onDelete && !isCuttable && (
        <button
          type="button"
          aria-label="Supprimer ce son"
          className="absolute top-0.5 right-0.5 z-20 flex h-4 w-4 items-center justify-center rounded-full bg-[#c8009c] text-white shadow-sm hover:bg-[#a8008a]"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}

      {/* Waveform visualizer */}
      {showWaveform && (
        <Waveform
          widthPx={Math.max(widthPx, 36)}
          color={border}
          url={waveformUrl}
          startFrac={waveformStartFrac}
          endFrac={waveformEndFrac}
        />
      )}

      {/* Label — hidden when too short */}
      {heightPx >= 24 && (
        <div className="flex items-center gap-2 px-3 h-full pr-4 relative z-[1]">
          {variant === "Ajouter" || variant === "AjouterAmbient" ? (
            <span className="flex items-center gap-1 text-[13px] font-normal whitespace-nowrap">
              <Plus className="h-3 w-3 shrink-0" />
              {label}
            </span>
          ) : (
            <span className="text-[13px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
              {label}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
